import { Check, X, Info } from "@phosphor-icons/react";

const PHASES = ["Onboarding", "Quote", "Design", "Review", "Final Proof Sign-Off", "Production", "Shipping/Go Live", "Completed"];

/**
 * Visual timeline of job phases.
 * - Circles with animations: checkmark (done), pulsing dot (current), X (rejected/rewind), empty (future)
 * - Clicking a completed or current phase invokes onSelectPhase(phaseName)
 * - selectedPhase highlights the chosen circle regardless of current
 */
export default function JobTimeline({
  currentPhase,
  selectedPhase,
  onSelectPhase,
  hadRevisions = false,   // Design was revisited
  hadRejection = false,   // Final Proof was rejected
  reachedPhases = null,   // Optional: object mapping phase -> boolean forcing clickable
  showHelp = true,
}) {
  const currentIdx = PHASES.indexOf(currentPhase);

  return (
    <div className="w-full" data-testid="job-timeline">
      <div className="relative overflow-x-auto pb-2">
        {/* Base connector line */}
        <div className="absolute top-5 left-0 right-0 h-[2px] bg-[var(--gc-border)]" />
        {/* Filled progress line */}
        <div
          className="absolute top-5 left-0 h-[2px] bg-[var(--gc-orange)] transition-all duration-700 ease-out"
          style={{ width: `${currentIdx === -1 ? 0 : (currentIdx / (PHASES.length - 1)) * 100}%` }}
        />

        <div className="relative flex justify-between items-start gap-2 min-w-[720px]">
          {PHASES.map((p, i) => {
            const done = i < currentIdx || currentPhase === "Completed";
            const active = i === currentIdx;
            const reached = reachedPhases?.[p] === true;
            const clickable = done || active || reached;
            const selected = p === selectedPhase;
            const rejected = (hadRevisions && (p === "Design" || p === "Review")) || (hadRejection && p === "Final Proof Sign-Off");

            let circleClasses = "w-10 h-10 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-300 relative ";
            let icon = null;

            if (done) {
              circleClasses += "bg-[var(--gc-orange)] border-[var(--gc-orange)] text-white";
              icon = <Check size={18} weight="bold" />;
            } else if (active) {
              circleClasses += "bg-white border-[var(--gc-orange)] text-[var(--gc-orange)] shadow-[0_0_0_6px_rgba(240,105,26,0.15)]";
              icon = <span className="w-3 h-3 rounded-full bg-[var(--gc-orange)] animate-pulse" />;
            } else {
              circleClasses += "bg-white border-[var(--gc-border-strong)] text-[var(--gc-text-muted)]";
              icon = <span className="w-2 h-2 rounded-full bg-[var(--gc-border-strong)]" />;
            }

            if (selected) {
              circleClasses += " ring-2 ring-offset-2 ring-[var(--gc-navy)]";
            }

            return (
              <button
                key={p}
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onSelectPhase?.(p)}
                className={`flex flex-col items-center gap-2 min-w-[80px] group ${clickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                data-testid={`timeline-phase-${p.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                title={clickable ? `View ${p} details` : `${p} — not yet reached`}
              >
                <div className={circleClasses}>
                  {icon}
                  {rejected && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[var(--gc-danger)] text-white flex items-center justify-center border border-white" title="This phase was revisited">
                      <X size={9} weight="bold" />
                    </span>
                  )}
                </div>
                <div className="text-center">
                  <div className="font-mono-tech text-[10px] text-[var(--gc-text-muted)]">P{String(i + 1).padStart(2, '0')}</div>
                  <div className={`text-[11px] md:text-xs mt-0.5 leading-tight ${active ? 'text-[var(--gc-orange)] font-bold' : done ? 'text-[var(--gc-navy)] font-semibold' : 'text-[var(--gc-text-muted)]'} ${selected ? 'underline decoration-2 decoration-[var(--gc-navy)] underline-offset-2' : ''}`}>
                    {p}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {showHelp && (
        <div className="mt-4 flex items-start gap-2 text-xs text-[var(--gc-text-muted)]">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>Click any completed or current phase to view its details. Phases you haven&apos;t reached yet are not clickable.</span>
        </div>
      )}
    </div>
  );
}
